import { api } from './api';
import { authService } from './auth';
import type { AttendanceSummary, ClassSummary, StudentRecord } from '@/types';

export type RegistrationCheck = {
  number: string;
  available: boolean;
  occupant?: {
    firstName: string;
    lastName: string;
    left: boolean;
    className?: string | null;
  };
};

type AdminOverviewResponse = {
  students: {
    total: number;
    delta: number;
  };
  attendance: {
    rate: number;
    history: Array<AttendanceSummary & { present: number; absent: number }>;
  };
  teachers: {
    coverage: string;
  };
  classes: {
    distribution: Array<{ name: string; students: number; attendance: number }>;
  };
};

export type TeacherAccount = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  loginPassword?: string | null;
  photo?: string | null;
  phone?: string | null;
  status?: 'Active' | 'On Leave' | 'Terminated';
  joiningDate?: string | null;
  address?: string | null;
  qualifications?: string | null;
  cnic?: string | null;
  assignedClasses: ClassSummary[];
};

export type AttendanceTrendPoint = {
  month: string;
  label: string;
  startDate: string;
  endDate: string;
  present: number;
  absent: number;
  late: number;
  leave: number;
  total: number;
  rate: number;
};

export type TeacherAttendanceTrendResponse = {
  startDate: string;
  endDate: string;
  months: AttendanceTrendPoint[];
  summary: {
    present: number;
    absent: number;
    late: number;
    leave: number;
    total: number;
    rate: number;
  };
};

export type SectionEntry = {
  id: string;
  name: string;
  classCount: number;
};

export type ClassLevelEntry = {
  id: string;
  name: string;
  sortOrder: number;
  classCount: number;
};

export type AdminAttendanceStatus = 'PRESENT' | 'ABSENT' | 'LATE' | 'LEAVE';

export type AttendanceBatchPayload = {
  date: string;
  records: Array<{
    id: string;
    status: AdminAttendanceStatus;
  }>;
};

export type AttendanceBatchResponse = {
  date: string;
  saved: number;
  records: Array<{
    id: string;
    studentId?: string;
    teacherId?: string;
    status: AdminAttendanceStatus;
    date: string;
  }>;
};

export const adminService = {
  async fetchOverview() {
    console.log('Fetching admin overview...');
    try {
      const { data } = await api.get<AdminOverviewResponse>('/admin/overview');
      console.log('Admin overview received:', data);
      return data;
    } catch (error) {
      console.error('Error fetching admin overview:', error);
      throw error;
    }
  },

  async listClasses() {
    const { data } = await api.get<ClassSummary[]>('/admin/classes');
    return data;
  },

  async listStudents() {
    const { data } = await api.get<StudentRecord[]>('/admin/students');
    return data;
  },

  async nextRegistration() {
    const { data } = await api.get<{ registration: string }>('/admin/students/next-registration');
    return data;
  },

  async checkRegistration(number: string, ignoreStudentId?: string | null) {
    const { data } = await api.get<RegistrationCheck>('/admin/students/registration-check', {
      params: {
        number,
        ...(ignoreStudentId ? { ignoreStudentId } : {}),
      },
    });
    return data;
  },

  async changeRegistration(studentId: string, rollNumber: string) {
    const { data } = await api.put<{ id: string; rollNumber: string; status: string }>(
      `/admin/students/${studentId}/registration`,
      { rollNumber },
    );
    return data;
  },

  async listSections() {
    const { data } = await api.get<SectionEntry[]>('/admin/sections');
    return data;
  },

  async listClassLevels() {
    const { data } = await api.get<ClassLevelEntry[]>('/admin/class-levels');
    return data;
  },

  async createClassLevel(payload: { name: string; sortOrder?: number }) {
    const { data } = await api.post<ClassLevelEntry>('/admin/class-levels', payload);
    return data;
  },

  async updateClassLevel(levelId: string, payload: { name: string; sortOrder?: number }) {
    const { data } = await api.put<ClassLevelEntry>(`/admin/class-levels/${levelId}`, payload);
    return data;
  },

  async deleteClassLevel(levelId: string) {
    await api.delete(`/admin/class-levels/${levelId}`);
  },

  async createSection(payload: { name: string }) {
    const { data } = await api.post<SectionEntry>('/admin/sections', payload);
    return data;
  },

  async updateSection(sectionId: string, payload: { name: string }) {
    const { data } = await api.put<SectionEntry>(`/admin/sections/${sectionId}`, payload);
    return data;
  },

  async deleteSection(sectionId: string) {
    await api.delete(`/admin/sections/${sectionId}`);
  },

  async listTeachers() {
    const { data } = await api.get<TeacherAccount[]>('/admin/teachers');
    return data;
  },

  async createClass(payload: { name?: string; gradeLevel: string; sectionId?: string; teacherId?: string }) {
    const { data } = await api.post<ClassSummary>('/admin/classes', payload);
    return data;
  },

  async updateClass(classId: string, payload: { name?: string; gradeLevel?: string; sectionId?: string | null; teacherId?: string | null }) {
    const { data } = await api.put<ClassSummary>(`/admin/classes/${classId}`, payload);
    return data;
  },

  async deleteClass(classId: string) {
    await api.delete(`/admin/classes/${classId}`);
  },

  async createStudent(payload: Record<string, unknown>) {
    const { data } = await api.post<StudentRecord>('/admin/students', payload);
    return data;
  },

  async updateStudent(studentId: string, payload: Record<string, unknown>) {
    const { data } = await api.put<StudentRecord>(`/admin/students/${studentId}`, payload);
    return data;
  },

  async createTeacher(payload: { firstName: string; lastName: string; email: string; password: string; classId?: string | null; phone?: string; address?: string; qualifications?: string; photo?: string; cnic?: string }) {
    const { data } = await api.post<{ id: string; firstName: string; lastName: string; email: string; role: string; photo?: string }>('/admin/teachers', payload);
    return data;
  },

  async updateTeacher(teacherId: string, payload: { firstName?: string; lastName?: string; email?: string; password?: string; classId?: string | null; phone?: string; address?: string; qualifications?: string; photo?: string; cnic?: string }) {
    const { data } = await api.put<TeacherAccount>(`/admin/teachers/${teacherId}`, payload);
    return data;
  },

  async deactivateTeacher(teacherId: string) {
    const { data } = await api.post<{ message: string; id: string; status: string }>(`/admin/teachers/${teacherId}/deactivate`);
    return data;
  },

  async restoreTeacher(teacherId: string) {
    const { data } = await api.post<{ message: string; id: string; status: string }>(`/admin/teachers/${teacherId}/restore`);
    return data;
  },

  async uploadAvatar(file: File) {
    const formData = new FormData();
    formData.append('avatar', file);
    const { data } = await api.post<{ url: string }>('/upload/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },

  async deleteStudent(id: string) {
    const { data } = await api.delete<{ message: string; id: string; status: string }>(`/admin/students/${id}`);
    return data;
  },

  async restoreStudent(id: string) {
    const { data } = await api.post<{ message: string; id: string; status: string }>(`/admin/students/${id}/restore`);
    return data;
  },

  async bulkAssignStudents(payload: { studentIds: string[]; classId: string | null }) {
    const { data } = await api.post<{ updated: number }>('/admin/students/bulk-assign', payload);
    return data;
  },

  async upsertStudent(payload: { id?: string } & Omit<StudentRecord, 'id'>) {
    const { data } = await api.post<StudentRecord>('/admin/students', payload);
    return data;
  },

  async removeStudent(id: string) {
    await api.delete(`/admin/students/${id}`);
  },

  async getStudentDailyAttendance(params: { classId?: string, sectionId?: string, teacherId?: string, gradeLevel?: string, period?: 'daily' | 'weekly' | 'monthly' | 'yearly', startDate?: string, endDate?: string }) {
    const { data } = await api.get('/admin/students/daily-attendance', { params });
    return data;
  },

  async getStudentAttendanceForDate(params: { date: string; classId?: string; sectionId?: string; teacherId?: string; gradeLevel?: string }) {
    const { date, ...rest } = params;
    const { data } = await api.get('/admin/students/daily-attendance', {
      params: {
        ...rest,
        period: 'daily',
        startDate: date,
        endDate: date,
      },
    });
    return data;
  },

  async saveStudentAttendanceBatch(payload: AttendanceBatchPayload) {
    const { data } = await api.post<AttendanceBatchResponse>('/admin/attendance/students/batch', payload);
    return data;
  },

  async saveTeacherAttendanceBatch(payload: AttendanceBatchPayload) {
    const { data } = await api.post<AttendanceBatchResponse>('/admin/attendance/teachers/batch', payload);
    return data;
  },

  async getAvailableYears() {
    const { data } = await api.get<{ min: number; max: number }>('/admin/attendance/years');
    return data;
  },

  async getSchoolAttendanceTrend(params?: { months?: number }) {
    const { data } = await api.get<{
      startDate: string;
      endDate: string;
      months: AttendanceTrendPoint[];
    }>('/admin/school/attendance-trend', { params });
    return data;
  },

  async getTeacherAttendanceTrend(params?: { months?: number }) {
    const { data } = await api.get<TeacherAttendanceTrendResponse>('/admin/teachers/attendance-trend', { params });
    return data;
  },

  async getStudentRankings(type: 'best' | 'worst', limit = 5, period = 'monthly') {
    const { data } = await api.get<Array<{
      id: string;
      name: string;
      className: string;
      photo?: string;
      attendanceRate: number;
      totalPresent: number;
      totalLate: number;
      totalAbsent: number;
      totalDays: number;
    }>>('/admin/students/ranking', { params: { type, limit, period } });
    return data;
  },

  async getTeacherRankings(type: 'best' | 'worst', limit = 5, period = 'monthly') {
    const { data } = await api.get<Array<{
      id: string;
      name: string;
      email: string;
      photo?: string;
      attendanceRate: number;
      totalPresent: number;
      totalLate: number;
      totalAbsent: number;
      totalDays: number;
    }>>('/admin/teachers/ranking', { params: { type, limit, period } });
    return data;
  },

  async getAttendanceSummary(classId: string, params: { period?: 'daily' | 'weekly' | 'monthly' | 'yearly'; startDate?: string; endDate?: string }) {
    const { data } = await api.get<{
      period: string;
      startDate: string;
      endDate: string;
      summary: {
        total: number;
        total_records?: number;
        expected_records?: number;
        missed?: number;
        present: number;
        absent: number;
        late: number;
        leave: number;
        messageSent: number;
      };
      students: Array<{
        id: string;
        name: string;
        present: number;
        absent: number;
        late: number;
        leave: number;
        total: number;
        missed?: number;
      }>;
      records: Array<{
        id: string;
        studentId: string;
        studentName: string;
        className: string;
        status: string;
        date: string;
        smsSentAt?: string | null;
      }>;
    }>(`/admin/classes/${classId}/attendance-summary`, { params });
    return data;
  },

  async getTeacherDetail(teacherId: string, params: { period?: 'daily' | 'weekly' | 'monthly' | 'yearly'; startDate?: string; endDate?: string }) {
    const { data } = await api.get<{
      teacher: {
        id: string;
        firstName: string;
        lastName: string;
        email: string;
        photo?: string;
        phone?: string;
        address?: string;
        qualifications?: string;
      };
      classes: Array<{
        id: string;
        name: string;
        gradeLevel: string;
        studentCount: number;
      }>;
      attendance: {
        total: number;
        present: number;
        absent: number;
        late: number;
        leave: number;
      };
      period: string;
      startDate: string;
      endDate: string;
    }>(`/admin/teachers/${teacherId}/detail`, { params });
    return data;
  },

  async getSchoolAttendanceSummary(params: { period?: 'daily' | 'weekly' | 'monthly' | 'yearly'; startDate?: string; endDate?: string; sectionId?: string; teacherId?: string }) {
    const { data } = await api.get<{
      period: string;
      startDate: string;
      endDate: string;
      summary: {
        total: number;
        total_records?: number;
        expected_records?: number;
        missed?: number;
        present: number;
        absent: number;
        late: number;
        leave: number;
        messageSent: number;
      };
      classes: Array<{
        id: string;
        name: string;
        present: number;
        absent: number;
        late: number;
        leave: number;
        total: number;
      }>;
      records: Array<{
        id: string;
        studentId: string;
        studentName: string;
        className: string;
        status: string;
        date: string;
        smsSentAt?: string | null;
      }>;
    }>('/admin/school/attendance-summary', { params });
    return data;
  },

  async getTeacherAttendanceSummary(params: {
    period?: 'daily' | 'weekly' | 'monthly' | 'yearly';
    startDate?: string;
    endDate?: string;
    classId?: string;
    sectionId?: string;
    gradeLevel?: string;
  }) {
    const fetchSummary = () => api.get<{
      period: string;
      startDate: string;
      endDate: string;
      summary: {
        total: number;
        total_records?: number;
        expected_records?: number;
        present: number;
        absent: number;
        late: number;
        leave: number;
        missed: number;
      };
      teachers: Array<{
        id: string;
        name: string;
        photo?: string | null;
        present: number;
        absent: number;
        late: number;
        leave: number;
        missed: number;
        total: number;
      }>;
      records: Array<{
        id: string;
        teacherId: string;
        teacherName: string;
        photo?: string | null;
        status: string;
        date: string;
      }>;
    }>('/admin/teachers/attendance-summary', { params });
    try {
      const { data } = await fetchSummary();
      return data;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401) {
        try {
          await authService.getCurrentUser();
          const { data } = await fetchSummary();
          return data;
        } catch (retryError) {
          throw retryError;
        }
      }
      throw error;
    }
  },

  async getTeacherAttendanceForDate(params: { date: string; classId?: string; sectionId?: string; gradeLevel?: string }) {
    const { date, ...rest } = params;
    const { data } = await api.get('/admin/teachers/attendance-summary', {
      params: {
        ...rest,
        period: 'daily',
        startDate: date,
        endDate: date,
      },
    });
    return data;
  },

  async getTeacherMonthlyAttendanceOverview(params: { year: number; month: number; classId?: string; sectionId?: string; gradeLevel?: string }) {
    const { data } = await api.get<Array<{
      date: string;
      day: number;
      present: number;
      absent: number;
      late: number;
      leave: number;
      missed: number;
      expected_records: number;
      total: number;
      percentage: number;
      isHoliday: boolean;
      holidayName: string | null;
      isOffDay: boolean;
    }>>('/admin/attendance/teacher-monthly-overview', { params });
    return data;
  },

  async getTeacherYearlyAttendanceOverview(params: { year: number; classId?: string; sectionId?: string; gradeLevel?: string }) {
    const { data } = await api.get<Array<{
      month: string;
      month_num: number;
      total_teachers: number;
      working_days: number;
      expected_records: number;
      recorded_records: number;
      present: number;
      absent: number;
      late: number;
      leave: number;
      missed: number;
      percentage: number;
    }>>('/admin/attendance/teacher-yearly-overview', { params });
    return data;
  },

  async getStudentYearlyAttendanceOverview(params: { year: number; classId?: string; sectionId?: string; teacherId?: string, gradeLevel?: string }) {
    const { data } = await api.get<{
      summary: {
        total_students: number;
        working_days: number;
        expected_records: number;
        recorded_records: number;
        present: number;
        absent: number;
        late: number;
        leave: number;
        missed: number;
      };
      months: Array<{
        month: string;
        month_num: number;
        total_students: number;
        working_days: number;
        expected_records: number;
        recorded_records: number;
        present: number;
        absent: number;
        late: number;
        leave: number;
        missed: number;
        percentage: number;
      }>;
    }>('/admin/attendance/student-yearly-overview', { params });
    return data;
  },



  async getMonthlyAttendanceOverview(params: { year: number; month: number; classId?: string; sectionId?: string; teacherId?: string, gradeLevel?: string }) {
    const { data } = await api.get<Array<{
      date: string;
      day: number;
      present: number;
      absent: number;
      late: number;
      leave: number;
      missed: number;
      expected_records: number;
      total: number;
      percentage: number;
      isHoliday: boolean;
      holidayName: string | null;
      isOffDay: boolean;
    }>>('/admin/attendance/monthly-overview', { params });
    return data;
  },

  async getHolidays(params?: { year?: number }) {
    const { data } = await api.get<Array<{
      id: string;
      name: string;
      start_date: string;
      end_date: string;
      type: string;
      class_id: string | null; // Keep for backward compatibility if backend sends it
      classes?: Array<{ id: string; name: string }>;
    }>>('/admin/holidays', { params });
    return data;
  },

  async createHoliday(payload: { name: string; start_date: string; end_date: string; type?: string; class_ids?: string[] }) {
    const { data } = await api.post('/admin/holidays', payload);
    return data;
  },

  async updateHoliday(id: string, payload: { name: string; start_date: string; end_date: string; type?: string; class_ids?: string[] }) {
    const { data } = await api.put(`/admin/holidays/${id}`, payload);
    return data;
  },

  async deleteHoliday(id: string) {
    const { data } = await api.delete(`/admin/holidays/${id}`);
    return data;
  },

  async getSettings() {
    const { data } = await api.get<Record<string, any>>('/admin/settings');
    return data;
  },

  async saveSettings(settings: Record<string, any>) {
    const { data } = await api.post<{ message: string; settings: Record<string, any> }>('/admin/settings', { settings });
    return data;
  },

  async getStudentAttendanceHistory(studentId: string, month?: string) {
    const { data } = await api.get(`/admin/students/${studentId}/attendance`, { params: { month } });
    return data;
  },

  async getTeacherAttendanceHistory(teacherId: string, month?: string) {
    const { data } = await api.get(`/admin/teachers/${teacherId}/history`, { params: { month } });
    return data;
  },

  async getStudentYearlyHistory(studentId: string, year: number) {
    const { data } = await api.get(`/admin/students/${studentId}/yearly-attendance`, { params: { year } });
    return data;
  },

  async getTeacherYearlyHistory(teacherId: string, year: number) {
    const { data } = await api.get(`/admin/teachers/${teacherId}/yearly-attendance`, { params: { year } });
    return data;
  },
};
